import React from 'react';
import {
  Box,
  Grid,
  IconButton,
  styled,
  Typography
} from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import { useFieldArray } from "react-hook-form";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import {
  colors,
  fontWeight,
  gaps,
} from 'theme/defaultStyle';
import AddLink from "controls/global/add-link/AddLink";
import CustomTooltip from "controls/global/custom-tooltip";
import AgencyLocationField from "../../components/AgencyLocationField";
import { isCompanyLocationType } from 'pages/assign/new/InventoryAssignNewHelper';
import { getNextSharingAgencyItem } from "./SharingAgencyHelper";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { SharingAgencyItem } from "entities/UIModel/SharingAgencyItem";
import { hasValue } from 'utils/shared';

const schema = "sharingAgencyItems";

const ContainerBox = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  "& .gridStyle": {
    paddingBottom: "0 !important" as any,
  },
});

const RowBox = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: gaps.small1,
});

const AddShareDiv = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
});

const SharingAgencyGrid = () => {
  const { nameString, watch, setValue, clearErrors, getValues } = useFormWrapper();

  const { fields, append, remove } = useFieldArray({
    name: nameString(schema),
    keyName: "rowKey",
  });

  const sharingAgencyItems: SharingAgencyItem[] =
    watch(nameString(schema), fields) || [];

  const assignedLocationUniqueID = watch("locationUniqueID");
  const assignedLocationLegacyID = watch("locationLegacyID");
  const assignedAgencySelected = hasValue(assignedLocationUniqueID);

  // Build the list of agency ids which hides from users.
  const getExcludeIds = (currentIndex: number): string[] => {
    const ids: string[] = [];
    if (assignedLocationUniqueID) ids.push(assignedLocationUniqueID);
    sharingAgencyItems.forEach((item, i) => {
      if (i !== currentIndex && item?.locationUniqueID) {
        ids.push(item.locationUniqueID);
      }
    });
    return ids;
  };

  const lastItem = sharingAgencyItems[sharingAgencyItems.length - 1];
  // A sharing agency is only "selected" once a real location is picked from the
  const addAgencyDisabled = !assignedAgencySelected || !lastItem?.locationUniqueID;
  const deleteItemDisabled = sharingAgencyItems.length === 1;

  const clearAgencyLocation = (index: number) => {
    setValue(`${schema}.${index}.selectedLocationFullDisplayName`, "");
    setValue(`${schema}.${index}.companyID`, "");
    setValue(`${schema}.${index}.companyName`, "");
    setValue(`${schema}.${index}.companyLegacyID`, "");
    setValue(`${schema}.${index}.locationUniqueID`, "");
    setValue(`${schema}.${index}.locationLegacyID`, "");
    setValue(`${schema}.${index}.locationDisplayName`, "");
    setValue(`${schema}.${index}.locationFullDisplayName`, "");
    setValue(`${schema}.${index}.locationStateCode`, "");
    setValue(`${schema}.${index}.locationStateAbbr`, "");
    setValue(`${schema}.${index}.isActive`, 1);
  };

  const handleLocationChange = (
    location: CompanyLocation | string,
    index: number
  ) => {
    if (!location || typeof location === "string") {
      clearAgencyLocation(index);
      setValue(`${schema}.${index}.locationFullDisplayName`, location);
      return;
    }

    if (location && isCompanyLocationType(location)) {
      clearErrors(`${schema}.${index}.locationFullDisplayName`);
      setValue(`${schema}.${index}.selectedLocationFullDisplayName`, location?.locationFullDisplayNameWithStatus);
      setValue(`${schema}.${index}.companyID`, location?.companyID);
      setValue(`${schema}.${index}.companyName`, location?.companyName);
      setValue(`${schema}.${index}.companyLegacyID`, location?.companyLegacyID);
      setValue(`${schema}.${index}.locationUniqueID`, location?.locationUniqueID);
      setValue(`${schema}.${index}.locationLegacyID`, location?.locationLegacyID);
      setValue(`${schema}.${index}.locationDisplayName`, location?.locationDisplayName);
      setValue(`${schema}.${index}.locationFullDisplayName`, location?.locationFullDisplayNameWithStatus);
      setValue(`${schema}.${index}.locationStateCode`, location?.locationStateCode);
      setValue(`${schema}.${index}.locationStateAbbr`, location?.locationStateAbbr);
      setValue(`${schema}.${index}.isActive`, hasValue(location?.isActive) ? Number(location?.isActive) : 1);
    }
  };

  const handleLocationBlur = (event: any, index: number) => {
    const currentItems: SharingAgencyItem[] = getValues(schema) || [];
    if (index >= currentItems.length) return;

    const newInputValue = event?.target?.value;
    const locationUniqueID = getValues(`${schema}.${index}.locationUniqueID`);
    const selectedLocationFullDisplayName = getValues(`${schema}.${index}.selectedLocationFullDisplayName`);
    if (
      !locationUniqueID ||
      !newInputValue ||
      !selectedLocationFullDisplayName ||
      newInputValue?.toLowerCase() !== selectedLocationFullDisplayName?.toLowerCase()
    ) {
      clearAgencyLocation(index);
    }
  };

  const addAgency = () => {
    if (addAgencyDisabled) return;
    append(getNextSharingAgencyItem(sharingAgencyItems));
  };

  const handleOnDeleteItem = (index: number) => {
    remove(index);
  };

  return (
    <>
      <ContainerBox>
        <Grid item xs={12} md={6} className={"gridStyle"}>
          <Typography sx={{ color: colors.blue15, fontWeight: fontWeight.bold2, mb: 2, mt: 2 }}>
            Sharing Agency
          </Typography>
          {fields.map((field, index) => (
            <RowBox key={(field as any).rowKey}>
              <Box sx={{ flexGrow: 1, mb:2 }}>
                <AgencyLocationField
                  label="Sharing Agency/Legacy ID (Location)"
                  name={`${schema}.${index}.locationFullDisplayName`}
                  onChange={(location: CompanyLocation | string) => handleLocationChange(location, index)}
                  onBlur={(event: React.ChangeEvent<any>) => handleLocationBlur(event, index)}
                  onClear={() => clearAgencyLocation(index)}
                  disabled={!assignedAgencySelected}
                  excludeIds={getExcludeIds(index)}
                  assignedLocationUniqueID={assignedLocationUniqueID}
                  agencyKey={assignedLocationLegacyID}
                  showAll
                />
              </Box>
              {!deleteItemDisabled && (
                <CustomTooltip title="Delete" placement="bottom" aria-label="Delete" arrow>
                  <IconButton
                    aria-label="Delete"
                    onClick={() => handleOnDeleteItem(index)}
                  >
                    <DeleteIcon sx={{ color: colors.blue15, position:"relative", top:-8 }} />
                  </IconButton>
                </CustomTooltip>
              )}
            </RowBox>
          ))}
        </Grid>
      </ContainerBox>
      <AddShareDiv>
        <AddLink onClick={addAgency} disabled={addAgencyDisabled}>
          Add Agency
        </AddLink>
      </AddShareDiv>
    </>
  );
};

export default SharingAgencyGrid;
